/**
 * Questionnaire metadata - tracks versions and stores responses
 */

export const QUESTIONNAIRE_VERSION = 'v1';
export const QUESTIONNAIRE_DATE = '2026-01-25';

// Role options for demographics
export const ROLE_OPTIONS = [
  'Product Manager',
  'Product Designer',
  'Product Marketing',
  'Product Operations',
  'Engineering Lead',
  'Founder',
  'UX Researcher',
  'Data/Analytics',
  'Other'
] as const;

// Years of experience options
export const EXPERIENCE_OPTIONS = [
  { value: '<1', label: 'Less than 1 year' },
  { value: '1-2', label: '1-2 years' },
  { value: '3-5', label: '3-5 years' },
  { value: '6-10', label: '6-10 years' },
  { value: '10+', label: '10+ years' }
] as const;

// List of countries (simplified, common ones first)
export const COUNTRY_OPTIONS = [
  'United States',
  'United Kingdom',
  'Canada',
  'Germany',
  'France',
  'Israel',
  'India',
  'Australia',
  'Netherlands',
  'Sweden',
  'Singapore',
  'Brazil',
  'Japan',
  'South Korea',
  'Spain',
  'Italy',
  'Poland',
  'Mexico',
  'Argentina',
  'South Africa',
  'United Arab Emirates',
  'Ireland',
  'Switzerland',
  'Belgium',
  'Austria',
  'Denmark',
  'Norway',
  'Finland',
  'New Zealand',
  'Portugal',
  'Czech Republic',
  'Romania',
  'Hungary',
  'Greece',
  'Turkey',
  'Indonesia',
  'Thailand',
  'Vietnam',
  'Philippines',
  'Malaysia',
  'China',
  'Taiwan',
  'Hong Kong',
  'Russia',
  'Ukraine',
  'Nigeria',
  'Kenya',
  'Egypt',
  'Chile',
  'Colombia',
  'Peru',
  'Other'
] as const;

export interface UserDemographics {
  country: string;
  role: string;
  customRole?: string; // If role is "Other"
  yearsExperience: string;
  inspirationalVoices: string[]; // Guest names, max 6
}

export interface UserQuizResponse {
  id: string; // UUID
  questionnaireVersion: string;
  timestamp: string;
  consentedToShare: boolean;
  demographics?: UserDemographics;
  answers: Record<string, number>;
  coordinates: number[]; // 20D embedding
}

/**
 * Generate a unique ID for the response
 */
export function generateResponseId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Save user response to localStorage
 */
export function saveUserResponse(response: UserQuizResponse): void {
  const responses = getStoredResponses();
  responses.push(response);
  localStorage.setItem('latentLenny_userResponses', JSON.stringify(responses));
}

/**
 * Get all stored responses from localStorage
 */
export function getStoredResponses(): UserQuizResponse[] {
  try {
    const stored = localStorage.getItem('latentLenny_userResponses');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Export all responses as JSON for download
 */
export function exportResponses(): string {
  const responses = getStoredResponses();
  return JSON.stringify({
    exportDate: new Date().toISOString(),
    questionnaireVersion: QUESTIONNAIRE_VERSION,
    totalResponses: responses.length,
    responses
  }, null, 2);
}
